import { type FormEvent, useState } from "react"
import { Button, ErrorNote, Field, Select } from "./ui"
import { type CompanyRole, api } from "../lib/api"
import { useAuth, useSession } from "../lib/auth"

/**
 * A person's identity: name, email, password. Global — the same login works in
 * every company the person belongs to — so no company edits it. The person
 * edits their own ("self", PATCH /auth/me, which asks for the current password
 * before changing the email or the password); staff edit anyone's ("platform").
 */
export function EmployeeAccount({ employee, scope, onSaved }: {
  employee: { id: string; name: string; email: string }
  scope: "self" | "platform"
  onSaved: () => void
}) {
  const isSelf = scope === "self"
  const { employee: signedInEmployee } = useSession()
  const { refresh, signOut } = useAuth()
  const [name, setName] = useState(employee.name)
  const [email, setEmail] = useState(employee.email)
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const needsCurrent = isSelf && (newPassword !== "" || email !== employee.email)

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSaved(false)
    setError(null)
    if (newPassword !== confirmPassword) {
      setError(new Error("The two password fields do not match."))
      return
    }
    if (newPassword && newPassword.length < 8) {
      setError(new Error("The new password must be at least 8 characters."))
      return
    }

    setSaving(true)
    try {
      await api.patch(isSelf ? "/auth/me" : `/platform/employees/${employee.id}`, {
        name,
        email,
        ...(newPassword ? { password: newPassword } : {}),
        ...(needsCurrent ? { currentPassword } : {}),
      })

      // Changing your own password intentionally invalidates this browser's
      // session along with every API token. Return to login rather than making
      // a follow-up request with a token we know is no longer valid.
      if (newPassword && signedInEmployee.id === employee.id) {
        signOut()
        return
      }

      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
      setSaved(true)
      onSaved()
      if (signedInEmployee.id === employee.id) await refresh()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={save} className="border-t border-border pt-3">
      <p className="text-sm font-medium text-foreground">Account details</p>
      <p className="mt-1 text-xs text-muted">
        Changes to name, email, and password apply wherever {isSelf ? "you use" : "this person uses"} Moderniser.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Name" value={name} onChange={(event) => setName(event.target.value)} maxLength={200} required />
        <Field label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          placeholder="Leave blank to keep"
          minLength={8}
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          placeholder="Repeat new password"
          minLength={8}
        />
        {needsCurrent && (
          <Field
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
          />
        )}
      </div>
      <p className="mt-2 text-xs text-faint">
        Setting a new password signs {isSelf ? "you" : "this person"} out on all devices and disconnects linked tools.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Button type="submit" variant="ghost" disabled={saving}>
          {saving ? "Saving…" : "Save account"}
        </Button>
        {saved && <p className="text-xs text-success">Account saved.</p>}
      </div>
      <div className="mt-2"><ErrorNote error={error} /></div>
    </form>
  )
}

/**
 * What a company admin controls about someone: their role in this company,
 * whether their access to it is switched on, and whether they are in it at
 * all. Their identity and their other companies are not this company's to
 * change.
 *
 * Access is the membership's own switch — it ends what a membership grants
 * without ending the membership, so the person keeps their place, role and
 * caps here and keeps working for any other company. The account-wide switch
 * is staff's; the one account edit a company makes is activating a person an
 * import brought in, who has never been set up (no password) — see
 * PATCH /auth/employees/:id, which only ever does that.
 */
export function CompanyMembership({ employee, onSaved }: {
  employee: { id: string; name: string; role: string; status: string; membershipStatus: "active" | "disabled"; neverSetUp: boolean }
  onSaved: () => void
}) {
  const [role, setRole] = useState<CompanyRole>(employee.role === "admin" || employee.role === "external" ? employee.role : "developer")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const accessOff = employee.membershipStatus === "disabled"

  async function run(action: () => Promise<unknown>) {
    setSaved(false)
    setError(null)
    setSaving(true)
    try {
      await action()
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border-t border-border pt-3">
      <p className="text-sm font-medium text-foreground">In this company</p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Select label="Role" value={role} onChange={(event) => setRole(event.target.value as CompanyRole)}>
          <option value="developer">Developer</option>
          <option value="external">External</option>
          <option value="admin">Admin</option>
        </Select>
        <Button variant="ghost" disabled={saving || role === employee.role} onClick={() => run(() => api.patch(`/auth/employees/${employee.id}`, { role }))}>
          Save role
        </Button>
        {accessOff ? (
          <Button
            disabled={saving}
            onClick={() => run(() => api.patch(`/auth/employees/${employee.id}/membership`, { status: "active" }))}
          >
            Enable access
          </Button>
        ) : (
          <Button
            variant="danger"
            disabled={saving}
            onClick={() => {
              if (!window.confirm(`Switch off ${employee.name}'s access to this company? They keep their place and role here, and any other company they work for is untouched.`)) return
              void run(() => api.patch(`/auth/employees/${employee.id}/membership`, { status: "disabled" }))
            }}
          >
            Disable access
          </Button>
        )}
        <Button
          variant="danger"
          disabled={saving}
          onClick={() => {
            if (!window.confirm(`Remove ${employee.name} from this company? Their work here stays; they stop being able to act for it. Someone left in no company at all is disabled.`)) return
            void run(() => api.delete(`/auth/employees/${employee.id}`))
          }}
        >
          Remove from company
        </Button>
        {saved && <p className="text-xs text-success">Saved.</p>}
      </div>
      {accessOff && (
        <p className="mt-2 text-xs text-warning">
          Access to this company is switched off: they cannot sign in to work here until it is enabled.
        </p>
      )}
      {employee.status === "disabled" && (
        employee.neverSetUp ? (
          <div className="mt-3 border-t border-border pt-3">
            <p className="text-sm font-medium text-foreground">Account</p>
            <p className="mt-1 text-xs text-muted">
              This account has never been set up — people an import brought in arrive this way, switched off and with no
              password. Set a first password to switch it on, then hand it to them.
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <Field
                label="First password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                minLength={8}
                placeholder="At least 8 characters"
              />
              <Button
                variant="ghost"
                disabled={saving || password.length < 8}
                onClick={() =>
                  void run(async () => {
                    await api.patch(`/auth/employees/${employee.id}`, { status: "active", password })
                    setPassword("")
                  })
                }
              >
                Activate account
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-xs text-muted">
            This person's account is switched off by Moderniser staff.
          </p>
        )
      )}
      <div className="mt-2"><ErrorNote error={error} /></div>
    </div>
  )
}
